/**
 * PROTOTYPE ONLY.
 *
 * Variant C — "Task list all the way down". Inverts the doc's core principle. The task
 * list is the primary surface for creating AND for editing: you land on it with the
 * required items empty, click into a full-page editor for each, and come back. Create
 * unlocks once the Required items are filled. After creation the same screen carries
 * on, with the status flipped to Active.
 *
 * The bet being tested: section 10 claims "I want to create" and "I want to change
 * something" need different patterns. If this variant feels fine, they don't.
 */
import {useState} from "react";
import {Alert, Button, Card, Divider, Space, Tag, Typography} from "antd";
import {
  Customisation,
  DraftSetter,
  ImportanceTag,
  SECTIONS,
  SectionFields,
  SectionKey,
  SessionDraft,
  customisationItems,
  sectionOneLiner,
} from "./shared";
import {applyCustomisation} from "./variant-a-wizard";

const {Title, Text, Paragraph} = Typography;

/** Only these block creation. Everything else can be filled in later. */
const REQUIRED_SECTIONS: SectionKey[] = ["general", "pricing", "availability"];

interface VariantProps {
  draft: SessionDraft;
  set: DraftSetter;
  customisation: Customisation;
  setCustomisation: (next: Customisation) => void;
}

export function VariantCTaskList({
  draft,
  set,
  customisation,
  setCustomisation,
}: VariantProps) {
  const [editing, setEditing] = useState<SectionKey | null>(null);
  const [visited, setVisited] = useState<SectionKey[]>([]);
  const [created, setCreated] = useState(false);

  if (editing) {
    const section = SECTIONS.find((entry) => entry.key === editing);

    return (
      <Card>
        <Button type="link" style={{paddingLeft: 0}} onClick={() => setEditing(null)}>
          ← Back to session setup
        </Button>
        <Title level={4}>{section?.title}</Title>
        <Paragraph type="secondary">{section?.blurb}</Paragraph>
        <Divider />
        <SectionFields section={editing} draft={draft} set={set} />
        <Divider />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              setVisited((current) =>
                current.includes(editing) ? current : [...current, editing]
              );
              setEditing(null);
            }}
          >
            Save and continue
          </Button>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
        </Space>
      </Card>
    );
  }

  const missing = REQUIRED_SECTIONS.filter((key) => !visited.includes(key));

  return (
    <Card>
      <div style={{display: "flex", justifyContent: "space-between", gap: 16}}>
        <div>
          <Title level={4} style={{marginTop: 0, marginBottom: 4}}>
            {created ? draft.name || "Your session" : "Set up your session"}
          </Title>
          <Text type="secondary">
            {created
              ? "Manage your session details and booking experience."
              : "Work through these in any order. You can change all of it later."}
          </Text>
        </div>
        <Tag color={created ? "green" : "default"}>
          {created ? "Active" : "Not created"}
        </Tag>
      </div>

      {created ? (
        <Alert
          type="success"
          showIcon
          style={{marginTop: 16}}
          message="Live and accepting bookings"
        />
      ) : null}

      <Title level={5} style={{marginTop: 24}}>
        Session
      </Title>
      {SECTIONS.map((section) => {
        const isRequired = REQUIRED_SECTIONS.includes(section.key);
        const isVisited = visited.includes(section.key);

        return (
          <Row
            key={section.key}
            title={section.title}
            status={
              isVisited || created ? sectionOneLiner(section.key, draft) : section.blurb
            }
            complete={isVisited || created}
            tag={
              isRequired && !isVisited && !created ? (
                <ImportanceTag level="required" />
              ) : null
            }
            onClick={() => setEditing(section.key)}
          />
        );
      })}

      <Title level={5} style={{marginTop: 24}}>
        Booking experience
      </Title>
      {customisationItems(customisation).map((item) => (
        <Row
          key={item.key}
          title={item.title}
          status={item.blurb}
          complete={
            item.key === "checkoutQuestions"
              ? customisation.checkoutQuestions > 0
              : Boolean(customisation[item.key])
          }
          tag={<ImportanceTag level={item.level} />}
          onClick={() => setCustomisation(applyCustomisation(customisation, item.key))}
        />
      ))}

      {!created ? (
        <>
          <Divider />
          <Space direction="vertical" style={{width: "100%"}}>
            {missing.length ? (
              <Text type="secondary">
                Still needed:{" "}
                {missing
                  .map((key) => SECTIONS.find((entry) => entry.key === key)?.title)
                  .join(", ")}
              </Text>
            ) : (
              <Text type="secondary">
                Everything required is filled in. Anything left is Recommended or Optional
                and can wait.
              </Text>
            )}
            <Button
              type="primary"
              disabled={missing.length > 0}
              onClick={() => setCreated(true)}
            >
              Create session
            </Button>
          </Space>
        </>
      ) : null}
    </Card>
  );
}

function Row({
  title,
  status,
  complete,
  tag,
  onClick,
}: {
  title: string;
  status: string;
  complete: boolean;
  tag: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          onClick();
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 4px",
        borderBottom: "1px solid #f0f0f0",
        cursor: "pointer",
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 999,
          border: "1px solid " + (complete ? "#52c41a" : "#d9d9d9"),
          background: complete ? "#52c41a" : "transparent",
          color: "#fff",
          fontSize: 12,
          lineHeight: "18px",
          textAlign: "center",
          flexShrink: 0,
        }}
      >
        {complete ? "✓" : ""}
      </span>
      <div style={{flex: 1}}>
        <Space>
          <Text strong>{title}</Text>
          {tag}
        </Space>
        <br />
        <Text type="secondary" style={{fontSize: 12}}>
          {status}
        </Text>
      </div>
      <Text type="secondary">→</Text>
    </div>
  );
}
